<script lang="ts">
import type { IconName } from "@/design/icons";

/**
 * As DEZ origens de uma linha da paleta, e o vocabulário que as ordena.
 *
 * Seis são DESTINOS (uma tela do produto, uma TELA que este workspace desenhou,
 * um FUNIL, o cadastro cru de uma entidade, uma PEÇA de "Seu sistema" para o
 * administrador, uma rota de administração da instalação), três são RESULTADOS de busca no servidor e a última são AÇÕES. Sem
 * separação, "Contatos" a tela, "Maria" a pessoa e "+ contato" o comando chegavam
 * na mesma pilha e nada dizia qual era qual.
 *
 * ── POR QUE `screens` E `funnels` NASCERAM ────────────────────────────────
 *
 * Porque o menu já as oferecia e a paleta não: quem trabalha numa tela do
 * construtor ou num funil chegava lá pela lateral e NUNCA pelo ⌘K, que é a porta
 * de quem sabe o nome do lugar. A V2 tinha 83 comandos fixos justamente porque o
 * catálogo dela era fixo; aqui o catálogo é do tenant, e deixá-lo de fora era
 * deixar de fora exatamente a parte que ele construiu.
 */
export type PaletteSection =
	| "product"
	| "screens"
	| "funnels"
	| "entities"
	| "pieces"
	| "admin"
	| "contact"
	| "conversation"
	| "record"
	| "action";

/**
 * A ordem em que as seções aparecem.
 *
 * O que o TENANT construiu vem antes do bastidor, como na lateral: telas e funis
 * logo depois das telas do produto, e o cadastro cru da entidade (`/e/:code`)
 * depois deles — porque a entidade é onde se conserta o dado, e a tela é onde a
 * equipe trabalha.
 */
export const SECTION_ORDER: readonly PaletteSection[] = [
	"product",
	"screens",
	"funnels",
	"entities",
	"pieces",
	"admin",
	"contact",
	"conversation",
	"record",
	"action",
];

/** A chave de cada seção; a frase mora no dicionário, ao lado do resto do ⌘K. */
const SECTION_KEY: Record<PaletteSection, string> = {
	product: "palette.sections.product",
	screens: "palette.sections.screens",
	funnels: "palette.sections.funnels",
	entities: "palette.sections.entities",
	pieces: "palette.sections.pieces",
	admin: "palette.sections.admin",
	contact: "palette.sections.contact",
	conversation: "palette.sections.conversation",
	record: "palette.sections.record",
	action: "palette.sections.action",
};

/**
 * O traço e o tom de cada seção — o "badge de facet colorido por tipo" da V2,
 * escrito nos tokens da V3.
 *
 * A V2 autorava a cor de cada tipo em hexadecimal (azul contatos, verde conversas,
 * âmbar canais). Um hex não vira tema escuro sozinho, então aqui o que se copia é
 * a IDEIA — cada origem tem cor própria e a mesma cor sempre — e os seis tons são
 * os do `Badge`, que já viram os dois temas.
 */
const SECTION_LOOK: Record<PaletteSection, { icon: IconName; tone: BadgeTone }> = {
	product: { icon: "grid", tone: "accent" },
	screens: { icon: "layers", tone: "info" },
	funnels: { icon: "funnel", tone: "success" },
	entities: { icon: "lines", tone: "neutral" },
	pieces: { icon: "tree", tone: "accent" },
	admin: { icon: "wrench", tone: "warning" },
	contact: { icon: "addressBook", tone: "accent" },
	conversation: { icon: "envelope", tone: "info" },
	record: { icon: "document", tone: "neutral" },
	action: { icon: "bolt", tone: "success" },
};

export type BadgeTone = "neutral" | "success" | "warning" | "danger" | "info" | "accent";

export interface PaletteRow {
	readonly key: string;
	readonly label: string;
	readonly hint: string;
	/**
	 * O que a busca lê e a tela NÃO desenha.
	 *
	 * O `hint` acima é a segunda linha do item, e é curta por isso. As abas de
	 * Configurações precisam ser achadas por trinta sinônimos — "senha", "fuso
	 * horário", "tema", "cnpj" — que não cabem ali e não podem faltar na peneira.
	 */
	readonly terms?: string;
	readonly section: PaletteSection;
	/** Um destino: uma rota, para a paleta e o menu não poderem discordar. */
	readonly to?: string;
	/** Ou uma ação, que acontece SOBRE a tela atual e não navega. */
	readonly run?: () => void;
	/** O traço desta linha. Ausente, vale o da seção — que é o caso da esmagadora maioria. */
	readonly icon?: IconName;
	/**
	 * A INICIAL no lugar do traço, para o que é uma pessoa.
	 *
	 * A V2 desenhava avatar em contato e em usuário pelo mesmo motivo: numa lista
	 * de dez linhas iguais, o rosto é o que se acha antes de ler. Sem foto aqui —
	 * a busca devolve nome e id, não arquivo — mas a inicial com a cor do `seed` já
	 * separa duas Anas, que é o que o ícone repetido nunca fez.
	 */
	readonly avatar?: { readonly name: string; readonly seed: string };
	/** O chip à direita do rótulo: a ETAPA de um registro, hoje. */
	readonly badge?: { readonly text: string; readonly tone: BadgeTone };
}
</script>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Avatar from "@/design/primitives/Avatar.vue";
import Badge from "@/design/primitives/Badge.vue";
import Icon from "@/design/primitives/Icon.vue";
import { paletteMessages } from "./i18n/palette";
import { highlight } from "./palette-search";

/**
 * O desenho da lista da paleta — e só o desenho.
 *
 * As linhas chegam já filtradas, já cortadas e JÁ NUMERADAS: a seta anda numa
 * lista só, então nenhum grupo pode inventar uma numeração própria. `totals` diz
 * quantas existem por seção, que é o denominador do "8 de 23" — o corte era mudo,
 * e quem não via a entidade dava a busca por vazia.
 */
const props = defineProps<{
	rows: readonly PaletteRow[];
	totals: Partial<Record<PaletteSection, number>>;
	cursor: number;
	/** O que foi digitado — para grifar no rótulo o pedaço que casou (o `ts_headline` da V2). */
	term?: string;
	/** O servidor ainda respondendo, com o termo já longo o bastante. */
	searching?: boolean;
	/** O que o servidor não alcançou, dito sob os registros. Vazio quando alcançou. */
	note?: string;
	/** As saídas oferecidas quando nada casou. Vazio quando não há o que sugerir. */
	suggestions?: readonly PaletteRow[];
}>();

const emit = defineEmits<{
	choose: [row: PaletteRow];
	hover: [index: number];
	/** "Ver mais" numa seção cortada — quem corta é a paleta, e é ela que abre. */
	more: [section: PaletteSection];
}>();

/** As seções que a paleta corta deste lado; as do servidor chegam cortadas de lá. */
const EXPANDABLE = new Set<PaletteSection>(["product", "screens", "funnels", "entities", "pieces", "admin"]);

const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: paletteMessages });

const groups = computed(() => {
	const indexed = props.rows.map((row, index) => ({ row, index }));
	// A ordem em que a paleta entregou as seções (ela ranqueia pelo nome): a seta
	// e o Enter andam por `rows`, e o desenho tem de andar junto.
	const order = SECTION_ORDER.filter((s) => props.rows.some((r) => r.section === s)).sort(
		(a, b) =>
			props.rows.findIndex((r) => r.section === a) - props.rows.findIndex((r) => r.section === b),
	);
	return order.map((section) => {
		const items = indexed.filter((entry) => entry.row.section === section);
		return {
			section,
			label: t(SECTION_KEY[section]),
			look: SECTION_LOOK[section],
			total: props.totals[section] ?? items.length,
			items,
		};
	}).filter((group) => group.items.length > 0);
});

/**
 * O rótulo partido no pedaço que casou.
 *
 * A V2 pedia o grifo ao Postgres (`ts_headline`) e o desenhava com `v-html`, o que
 * significa uma marcação vinda do servidor confiada pelo cliente. Aqui o grifo é
 * feito onde a pergunta está — o termo é digitado NESTA tela — e sai como nós de
 * texto: mesmo resultado à vista, sem uma porta de `v-html` na moldura de todas as
 * telas do painel.
 */
function parts(label: string) {
	return highlight(label, props.term ?? "");
}
</script>

<template>
	<!--
		O VAZIO, com uma saída.
		Uma linha cinza dizendo "nada corresponde" é o fim da conversa. A V2 desenhava
		lupa, frase e chips de sugestão — e os chips são a parte que importa: eles são
		o próximo clique de quem digitou errado.
	-->
	<template v-if="rows.length === 0">
		<li class="px-4 py-8 text-center">
			<Icon name="search" class="mx-auto size-8 text-fg-subtle opacity-60" />
			<p class="mt-3 text-sm font-medium text-fg">
				{{ searching ? t("palette.searching") : t("palette.empty") }}
			</p>
			<p v-if="!searching" class="mt-1 text-xs text-fg-subtle">{{ t("palette.emptyHint") }}</p>
			<div v-if="!searching && suggestions?.length" class="mt-4">
				<p class="text-2xs uppercase tracking-wide text-fg-subtle">
					{{ t("palette.suggestions") }}
				</p>
				<div class="mt-2 flex flex-wrap justify-center gap-1.5">
					<button
						v-for="suggestion in suggestions"
						:key="suggestion.key"
						type="button"
						class="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-raised px-2.5 py-1 text-xs text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg"
						@click="emit('choose', suggestion)"
					>
						<Icon :name="suggestion.icon ?? 'search'" class="size-3" />
						{{ suggestion.label }}
					</button>
				</div>
			</div>
		</li>
	</template>
	<template v-for="group in groups" :key="group.section">
		<li class="flex items-center justify-between gap-2 px-2.5 pb-1 pt-3 first:pt-1.5">
			<span class="text-xs font-medium text-fg-subtle">
				{{ group.label }}
			</span>
			<!-- O contador da seção, com a cor do tipo: é o `facets` da V2 — e o que
			     diz que oito não são todos. -->
			<Badge :tone="group.look.tone" class="px-1.5 py-0 font-mono text-3xs">
				{{ t("palette.counted", { shown: group.items.length, total: group.total }) }}
			</Badge>
		</li>
		<li v-for="entry in group.items" :key="entry.row.key">
			<button
				type="button"
				:data-index="entry.index"
				class="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left"
				:class="entry.index === cursor ? 'bg-bg-muted' : ''"
				@click="emit('choose', entry.row)"
				@mouseenter="emit('hover', entry.index)"
			>
				<Avatar
					v-if="entry.row.avatar"
					:name="entry.row.avatar.name"
					:seed="entry.row.avatar.seed"
					size="sm"
					class="shrink-0"
				/>
				<span
					v-else
					class="flex size-7 shrink-0 items-center justify-center rounded-md"
					:class="entry.index === cursor ? 'bg-surface-raised text-fg' : 'bg-bg-muted text-fg-subtle'"
				>
					<Icon :name="entry.row.icon ?? group.look.icon" class="size-4" />
				</span>
				<span class="min-w-0 flex-1">
					<span class="flex items-center gap-2">
						<span
							class="min-w-0 truncate text-sm text-fg"
						>
							<!-- O grifo, em nós de texto: o pedaço que casou em negrito, o resto igual. -->
							<template v-for="(part, i) in parts(entry.row.label)" :key="i">
								<b v-if="part.hit" class="font-semibold text-accent">{{ part.text }}</b>
								<template v-else>{{ part.text }}</template>
							</template>
						</span>
						<Badge v-if="entry.row.badge" :tone="entry.row.badge.tone" class="shrink-0 py-0">
							{{ entry.row.badge.text }}
						</Badge>
					</span>
					<!-- A SEGUNDA LINHA, e não uma coluna estreita à direita: telefone,
					     canal · trecho, entidade · etapa. Era ali que a V3 cortava o que a
					     V2 mostrava inteiro, porque uma coluna `shrink-0` de fonte mono
					     cabe uma palavra. -->
					<span v-if="entry.row.hint" class="mt-0.5 block truncate text-xs text-fg-subtle">
						{{ entry.row.hint }}
					</span>
				</span>
				<Icon
					v-if="entry.index === cursor"
					name="chevronRight"
					class="size-3.5 shrink-0 text-accent"
				/>
			</button>
		</li>
		<li v-if="EXPANDABLE.has(group.section) && group.total > group.items.length" class="px-2.5 pb-1">
			<button
				type="button"
				class="text-xs text-accent hover:underline"
				@click="emit('more', group.section)"
			>
				{{ t("palette.more", { n: group.total - group.items.length }) }}
			</button>
		</li>
	</template>
	<!-- Fora do laço de propósito: o alcance da busca importa MAIS quando ela não
	     achou nada, e ali a seção de registros nem chega a existir. -->
	<li v-if="note" class="px-2.5 pb-1 pt-1.5 text-xs text-fg-subtle">{{ note }}</li>
	<!-- Uma linha só, e só quando já há o que mostrar: o servidor respondendo
	     enquanto a lista de destinos está desenhada não pode apagá-la. -->
	<li v-if="searching && rows.length > 0" class="px-2.5 py-1.5 text-xs text-fg-subtle">
		{{ t("palette.searching") }}
	</li>
</template>
